import React, { useEffect, useState } from "react";
import {
  createFeedComment,
  deleteFeedComment,
  fetchFeedCommentFeedback,
  fetchFeedComments,
  voteFeedComment,
} from "../../api";
import { normalize } from "../typing/arabicText";
import { stripHarakat } from "../CallChat";
import TappableArabic from "../TappableArabic";
import { arabicDigits, arabicRelativeTime } from "../../utils/arabicNumbers";
import FushaFeedback from "./FushaFeedback";
import { avatarTint } from "./PostCard";
import useArabicFieldRemap from "../typing/useArabicFieldRemap";

// Mirrors feed.models.MAX_COMMENT_DEPTH — replies are hidden at the cap.
const MAX_DEPTH = 2;

/**
 * CommentThread — the mockup's nested thread under an expanded post.
 *
 * The API returns a FLAT ascending list with parent ids; this component
 * builds the tree (children arrays keyed by parent). A reply whose parent
 * isn't in the loaded set (moderated away) is promoted to top level with a
 * small note. Each node renders the mockup anatomy: monogram + collapse rail
 * (only when children exist), head (name · time), Amiri body, bare vote foot
 * (▲ n / ▼ / ردّ / حذف), and an inline reply composer with the same fuṣḥā
 * feedback loop as posts.
 */
export default function CommentThread({ post, canPost, currentUserId, isAdmin, showHarakat = true, onCountChange }) {
  const [comments, setComments] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null); // comment id | null (top-level box)
  const [collapsed, setCollapsed] = useState(() => new Set());
  // "ملاحظات لك" per comment: {id: reviews[] | "loading"} — author-only data
  // (my_feedback_count is always 0 on other people's comments).
  const [openNotes, setOpenNotes] = useState(() => new Set());
  const [notesById, setNotesById] = useState({});

  const toggleNotes = (comment) => {
    setOpenNotes((prev) => {
      const next = new Set(prev);
      if (next.has(comment.id)) next.delete(comment.id);
      else next.add(comment.id);
      return next;
    });
    if (notesById[comment.id] === undefined) {
      setNotesById((m) => ({ ...m, [comment.id]: "loading" }));
      fetchFeedCommentFeedback(comment.id)
        .then((d) => setNotesById((m) => ({ ...m, [comment.id]: d.reviews || [] })))
        .catch(() => setNotesById((m) => ({ ...m, [comment.id]: [] })));
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchFeedComments(post.id)
      .then((page) => {
        if (cancelled) return;
        setComments(page.results || []);
        setNextCursor(page.next_cursor);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [post.id]);

  const loadMore = () => {
    fetchFeedComments(post.id, nextCursor)
      .then((page) => {
        setComments((prev) => [...prev, ...(page.results || [])]);
        setNextCursor(page.next_cursor);
      })
      .catch(() => {});
  };

  const addComment = (comment) => {
    setComments((prev) => [...prev, comment]);
    onCountChange && onCountChange(post.id, +1);
  };

  const removeComment = async (comment) => {
    try {
      await deleteFeedComment(comment.id);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
      onCountChange && onCountChange(post.id, -1);
    } catch {
      /* best-effort */
    }
  };

  // Declarative optimistic comment vote (same pattern as posts).
  const voteComment = async (comment, direction) => {
    const desired = comment.my_vote === direction ? 0 : direction;
    const apply = (c, my_vote, like_count, down_count) =>
      c.id === comment.id
        ? { ...c, my_vote, like_count, down_count, score: like_count - down_count }
        : c;
    const up = comment.like_count + (desired === 1 ? 1 : 0) - (comment.my_vote === 1 ? 1 : 0);
    const down = comment.down_count + (desired === -1 ? 1 : 0) - (comment.my_vote === -1 ? 1 : 0);
    setComments((prev) => prev.map((c) => apply(c, desired, up, down)));
    try {
      const res = await voteFeedComment(comment.id, desired);
      setComments((prev) => prev.map((c) => apply(c, res.my_vote, res.like_count, res.down_count)));
    } catch {
      setComments((prev) =>
        prev.map((c) => apply(c, comment.my_vote, comment.like_count, comment.down_count))
      );
    }
  };

  // ── tree build: flat ascending list → roots + children map ──
  const byId = new Map(comments.map((c) => [c.id, c]));
  const children = new Map();
  const roots = [];
  for (const c of comments) {
    if (c.parent && byId.has(c.parent)) {
      if (!children.has(c.parent)) children.set(c.parent, []);
      children.get(c.parent).push(c);
    } else {
      // top level, or an orphan whose parent was moderated away — promoted.
      roots.push(c);
    }
  }

  const toggleCollapse = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderNode = (comment) => {
    const kids = children.get(comment.id) || [];
    const isCollapsed = collapsed.has(comment.id);
    const orphan = comment.parent && !byId.has(comment.parent);
    const mayDelete =
      comment.is_mine || post.is_mine || comment.author?.id === currentUserId || isAdmin;
    const isOp = comment.author?.id === (post.author?.id ?? -1);

    return (
      <div
        key={comment.id}
        className={`mj-comment flex gap-2.5 mt-3.5 ${comment.parent && !orphan ? "mj-nested" : ""}`}
      >
        {/* rail: monogram + ⊖ collapse toggle + thread line (children only) */}
        <div className="flex flex-col items-center">
          <span
            className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold shrink-0"
            style={{ ...avatarTint(comment.author?.id), border: "1px solid var(--mj-line)" }}
            aria-hidden="true"
          >
            {comment.author?.initial || "؟"}
          </span>
          {kids.length > 0 && (
            <>
              <button
                onClick={() => toggleCollapse(comment.id)}
                className="mj-collapse"
                aria-label={isCollapsed ? "توسيع الردود" : "طيّ الردود"}
                aria-expanded={!isCollapsed}
                title={isCollapsed ? "توسيع" : "طيّ"}
              >
                {isCollapsed ? "+" : "−"}
              </button>
              {!isCollapsed && (
                <button
                  onClick={() => toggleCollapse(comment.id)}
                  className="w-[1.5px] flex-1 mt-1.5 rounded-[1px]"
                  style={{ background: "var(--mj-line)" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "var(--mj-gold)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "var(--mj-line)")}
                  aria-label="طيّ الردود"
                  title="طيّ"
                />
              )}
            </>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* head */}
          <div className="flex items-center gap-1.5 flex-wrap text-[12.5px]" style={{ color: "var(--mj-muted)" }}>
            <b style={{ color: "var(--mj-cream)" }}>{comment.author?.display_name}</b>
            {isOp && (
              <span
                className="text-[10px] font-bold rounded-[5px] px-1.5"
                style={{ color: "var(--mj-ink-4)", border: "1px solid var(--mj-ink-6)" }}
              >
                صاحب المنشور
              </span>
            )}
            <span>· {arabicRelativeTime(comment.created_at)}</span>
            {orphan && (
              <span className="text-[11px]" style={{ color: "var(--mj-muted)" }}>
                · ردٌّ على تعليق محذوف
              </span>
            )}
          </div>

          {/* body */}
          <TappableArabic
            text={showHarakat ? comment.body : stripHarakat(comment.body)}
            className="font-arabic text-[18px] leading-[2] mt-1"
            style={{ color: "var(--mj-body)" }}
          />

          {/* foot: bare vote row + reply + delete */}
          <div className="flex items-center gap-4 mt-1.5 text-[12px] font-semibold" style={{ color: "var(--mj-muted)" }}>
            <button
              onClick={() => voteComment(comment, 1)}
              className="flex items-center gap-1.5 hover:brightness-125"
              style={comment.my_vote === 1 ? { color: "var(--mj-gold)" } : undefined}
              aria-label="صوّت لأعلى"
            >
              ▲ {arabicDigits(comment.score ?? 0)}
            </button>
            <button
              onClick={() => voteComment(comment, -1)}
              className="hover:brightness-125"
              style={comment.my_vote === -1 ? { color: "var(--mj-flag)" } : undefined}
              aria-label="صوّت لأسفل"
            >
              ▼
            </button>
            {canPost && comment.depth < MAX_DEPTH && (
              <button
                onClick={() => setReplyTo((cur) => (cur === comment.id ? null : comment.id))}
                className="hover:brightness-125"
                style={replyTo === comment.id ? { color: "var(--mj-cream)" } : undefined}
              >
                ردّ
              </button>
            )}
            {comment.is_mine && comment.my_feedback_count > 0 && (
              <button
                onClick={() => toggleNotes(comment)}
                className="hover:brightness-125"
                style={{ color: "var(--mj-gold)" }}
                title="ملاحظات المدقّق — لا يراها غيرُك"
                aria-expanded={openNotes.has(comment.id)}
              >
                ✎ ملاحظات لك
              </button>
            )}
            {mayDelete && (
              <button onClick={() => removeComment(comment)} className="hover:brightness-125">
                حذف
              </button>
            )}
            {kids.length > 0 && isCollapsed && (
              <button onClick={() => toggleCollapse(comment.id)} style={{ color: "var(--mj-ink-4)" }}>
                ⊕ {arabicDigits(kids.length)} {kids.length === 1 ? "ردّ" : "ردود"}
              </button>
            )}
          </div>

          {/* private coaching notes — author's eyes only */}
          {openNotes.has(comment.id) && (
            <div className="dark-scope">
              {notesById[comment.id] === "loading" && (
                <p className="text-xs mt-2" style={{ color: "var(--mj-muted)" }}>جارٍ التحميل…</p>
              )}
              {Array.isArray(notesById[comment.id]) &&
                notesById[comment.id].map((review) => (
                  <FushaFeedback key={review.id} feedback={review} />
                ))}
            </div>
          )}

          {/* inline reply composer */}
          {replyTo === comment.id && (
            <ReplyBox
              postId={post.id}
              parentId={comment.id}
              onPublished={(c) => {
                addComment(c);
                setReplyTo(null);
              }}
            />
          )}

          {/* nested children — recursion inside c-main gives the ~38px indent */}
          {!isCollapsed && kids.map((kid) => renderNode(kid))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="rounded-[14px] px-4 py-3 mt-2 font-plex"
      style={{ background: "var(--mj-surface)", border: "1px solid var(--mj-line)" }}
    >
      {/* composer first, thread below — per the mockup's reading order */}
      {canPost && replyTo === null && (
        <ReplyBox postId={post.id} parentId={null} onPublished={addComment} />
      )}

      {loading && (
        <p className="text-xs py-2" style={{ color: "var(--mj-muted)" }}>جارٍ التحميل…</p>
      )}
      {!loading && roots.length === 0 && (
        <p className="text-xs py-2 mt-2" style={{ color: "var(--mj-muted)" }}>
          لا تعليقات بعد — كن أوّل من يُجيب.
        </p>
      )}
      {!loading && roots.length > 0 && (
        <div className="mt-3 pt-1" style={{ borderTop: "1px solid var(--mj-line)" }}>
          {roots.map((c) => renderNode(c))}
        </div>
      )}

      {nextCursor && (
        <button
          onClick={loadMore}
          className="w-full text-[12px] font-semibold rounded-lg px-2 py-1.5 mt-3"
          style={{ background: "var(--mj-raised)", color: "var(--mj-muted)" }}
        >
          مزيد من التعليقات
        </button>
      )}
    </div>
  );
}

/**
 * ReplyBox — the mockup's comment-box: raised field in Amiri, with the same
 * fuṣḥā rejection loop as the post composer (rejection keeps the draft and
 * teaches inline via FushaFeedback).
 */
function ReplyBox({ postId, parentId, onPublished }) {
  const { remapKeyDown } = useArabicFieldRemap();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    const body = normalize(draft).trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const data = await createFeedComment(postId, body, parentId);
      if (data.comment.status === "published") {
        setDraft("");
        setFeedback(data.feedback);
        onPublished(data.comment);
      } else {
        setFeedback(data.feedback); // rejected: keep draft, teach inline
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="flex gap-2">
        <input
          dir="rtl"
          lang="ar"
          className="flex-1 rounded-xl px-4 py-2.5 font-arabic text-[17px] outline-none"
          style={{
            background: "var(--mj-raised)",
            border: "1px solid var(--mj-line)",
            color: "var(--mj-cream)",
          }}
          placeholder={parentId ? "رُدَّ بالفصحى…" : "أضِفْ تعليقًا — بالفصحى وحدَها…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") return submit();
            remapKeyDown(e, setDraft);
          }}
        />
        <button
          onClick={submit}
          disabled={busy || !draft.trim()}
          className="rounded-full px-4 py-1.5 text-[12.5px] font-bold disabled:opacity-50"
          style={{ background: "var(--mj-gold)", color: "var(--mj-bg)" }}
        >
          {busy ? "…" : parentId ? "ردّ" : "انشر"}
        </button>
      </div>
      {error && (
        <p className="text-xs mt-1 px-1" style={{ color: "var(--mj-flag)" }}>{error}</p>
      )}
      <div className="dark-scope">
        <FushaFeedback
          feedback={feedback}
          onApplySuggestion={(t, s) => setDraft((d) => d.replace(t, s))}
        />
      </div>
    </div>
  );
}
